import {
  CSSProperties,
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import {
  FiCreditCard,
  FiDollarSign,
  FiList,
  FiPlusCircle,
  FiShield,
  FiSliders,
  FiSmartphone,
} from "react-icons/fi";
import Swal from "sweetalert2";
import $ from "jquery";
import axios from "axios";
import { actionCreators as StudnetCreator } from "../../store/student";
import { url } from "../../store/initialStatus";
import DoPayment from "../onlinepayment/pay";
import { Translate } from "../../helpers/Translate";
import "./ewallet.css";
import DEFAULT_AVATAR from "@/assets/avatar.jpeg";

const t = new Translate();

// Full-screen overlay that hosts the online payment flow.
const paymentOverlayStyle: CSSProperties = {
  zIndex: 100,
  display: "block",
  background: "white",
  border: "0px none transparent",
  overflow: "hidden auto",
  visibility: "visible",
  margin: "0px",
  padding: "0px",
  position: "fixed",
  left: "0px",
  top: "0px",
  width: "100%",
  height: "100%",
};

// Resolves a student's photo: full URLs are used as-is, bare ids point to Google Drive.
const getAvatarSrc = (driveid?: string) => {
  if (!driveid) return DEFAULT_AVATAR;
  return driveid.includes("https://")
    ? driveid
    : `https://drive.google.com/uc?id=${driveid}&export=download`;
};

export default function EWallet() {
  const dispatch: Dispatch<any> = useDispatch();
  const Navigate = useNavigate();

  const students = useSelector((state: any) => state.Student.students);
  const guardian = useSelector((state: any) => state.Parent.guardian);

  // Modal state: `controlButton` switches between the "Control" and "Charge" modals.
  const [isModalOpen, ShowModal] = useState<boolean>(false);
  const [controlButton, setControlButton] = useState(false);
  const [modalTitle, SetModalHeader] = useState<any>(null);

  // Form state shared by both modals.
  const [SelectedStd, SelectStd] = useState<any>(null);
  // Never 0: the amount is what gets charged, and both modals require > 0.
  const [Amount, SetAmount] = useState<any>(1);
  const [restrict, setRestrict] = useState(0);
  const [toggler, setToggler] = useState(true);

  // When set, the payment component replaces the whole page.
  const [Pay, SetPayment] = useState<any>(null);

  useEffect(() => {
    dispatch(StudnetCreator.GetStudents({ id: guardian.id }));
  }, [dispatch, guardian.id]);

  const toggle = useCallback(() => ShowModal((open) => !open), []);
  const closeModal = useCallback(() => ShowModal(false), []);

  // Saves the daily limit and lock state, then refreshes the students list.
  const setStudentLimit = useCallback(
    (Amount: any, SelectedStd: any, restrict: any) => {
      Swal.showLoading();
      axios
        .post(`${url}payment/wallet/controle-wallet.php`, {
          Elimit: Amount,
          student_id: SelectedStd,
          Locked: restrict,
        })
        .then(() => {
          Swal.fire("Saved Successfully!", "", "success");
        })
        .then(() => {
          ShowModal(false);
          dispatch(StudnetCreator.GetStudents({ id: guardian.id }));
        });
    },
    [dispatch, guardian.id],
  );

  // Called by the payment component; on success, bumps the displayed balance.
  const paymentFinished = useCallback(
    (e: any) => {
      SetPayment(null);
      if (e === "success") {
        $("#" + SelectedStd + "balance").text(
          parseInt(Amount) + parseInt($("#" + SelectedStd + "balance").text()),
        );
      }
    },
    [SelectedStd, Amount],
  );

  const openChargeModal = useCallback((student: any) => {
    SelectStd(student.id);
    SetAmount(1);
    SetModalHeader(
      t.translate("Charging E-Wallet for") + ": " + student.fullname,
    );
    ShowModal(true);
    setControlButton(false);
  }, []);

  const openControlModal = useCallback((student: any) => {
    SelectStd(student.id);
    SetAmount(student.elimit);
    setRestrict(student.locked);
    setToggler(+student.locked === 0);
    SetModalHeader(
      t.translate("Set Maximum Credit Per Day") + ": " + student.fullname,
    );
    ShowModal(true);
    setControlButton(true);
  }, []);

  const onToggleActive = useCallback(() => {
    toggler ? setRestrict(1) : setRestrict(0);
    setToggler(!toggler);
  }, [toggler]);

  const showInvalidAmount = useCallback(() => {
    Swal.fire({
      title: "<p>Error!</p>",
      html: `<i>${t.translate("Please Enter a valid amount!")}</i>`,
      icon: "error",
    });
  }, []);

  const onSubmitLimit = useCallback(() => {
    Swal.showLoading();
    if (Amount > 0) {
      setStudentLimit(Amount, SelectedStd, restrict);
    } else {
      showInvalidAmount();
    }
  }, [Amount, SelectedStd, restrict, setStudentLimit, showInvalidAmount]);

  const onContinuePayment = useCallback(() => {
    if (Amount > 0) {
      SetModalHeader(t.translate("Initiating the Payment..."));
      SetPayment(
        <DoPayment
          amount={Amount}
          serviceName={
            t.translate("Charging E-Wallet for") + ": " + SelectedStd
          }
          std={SelectedStd}
          code="EW"
          onFinish={paymentFinished}
        />,
      );
      ShowModal(false);
    } else {
      showInvalidAmount();
    }
  }, [Amount, SelectedStd, paymentFinished, showInvalidAmount]);

  // The amount must stay positive: no sign characters, no pasted negatives.
  const blockNegativeKeys = useCallback((e: any) => {
    if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
  }, []);

  const onAmountChange = useCallback((e: any) => {
    if (Number(e.target.value) < 0) return;
    SetAmount(e.target.value);
  }, []);

  // Static intro content; strings are translated at render time.
  const features = useMemo(
    () => [
      {
        icon: <FiShield size={22} />,
        title:
          "A safe, simple, and smart way of managing your children’s finances",
        lines: [
          "IVY E-Wallet is a modern way of establishing the right balance of control and independence for your kids.",
          "This solution enables parents to view all their kids’ finances along with allowing them to set pocket money, tracking spending, monitoring savings goals, and locking cards.",
        ],
      },
      {
        icon: <FiSmartphone size={22} />,
        lines: [
          "Both parents and child have an app on their phones.",
          "Parents can simply add money to the wallet which they intend to give to their kids.",
          "Once the money is added, the child can use it like a normal debit card or a mobile wallet.",
        ],
      },
      {
        icon: <FiSliders size={22} />,
        lines: [
          "This solution also comes with a feature which enables parents to cap daily spend limits on the application.",
          "Moreover, it also allows parents to put a cap on expenditures across various categories.",
          "These features help parents to ensure that their kid is not overspending his/her pocket money.",
        ],
      },
    ],
    [],
  );

  const studentCards = useMemo(
    () =>
      students?.map((student: any) => {
        const isActive = +student.locked === 0;
        return (
          <div className="col-12 col-md-6 col-xl-4" key={student.id}>
            <div className="card ewallet-card border-0 shadow-sm h-100">
              {/* Header: photo, name, grade */}
              <div className="card-body d-flex align-items-center gap-3 pb-2">
                <img
                  src={getAvatarSrc(student.driveid)}
                  alt={student.fullname}
                  className="ewallet-avatar"
                />
                <div className="flex-grow-1 overflow-hidden">
                  <h5 className="mb-1 text-truncate" title={student.fullname}>
                    {student.fullname}
                  </h5>
                  <span className="badge rounded-pill bg-light text-dark border">
                    {t.translate("Grade")} : {student.grade}
                  </span>
                </div>
                <span
                  className={`badge rounded-pill ${
                    isActive ? "bg-success" : "bg-secondary"
                  }`}
                >
                  {isActive ? t.translate("Active") : t.translate("Suspended")}
                </span>
              </div>

              {/* Balance */}
              <div className="card-body pt-2">
                <div className="ewallet-balance">
                  <small className="d-block opacity-75">
                    {t.translate("Wallet Credit")}
                  </small>
                  <span className="fs-2 fw-bold">
                    <span id={`${student.id}balance`}>{student.balance}</span>{" "}
                    <small className="fs-6 fw-normal">
                      {t.translate("LE.")}
                    </small>
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="card-body pt-0 d-grid gap-2">
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary flex-fill d-inline-flex align-items-center justify-content-center gap-2"
                    onClick={() => openChargeModal(student)}
                  >
                    <FiPlusCircle /> {t.translate("Charge Wallet")}
                  </button>
                  <button
                    className="btn btn-outline-danger flex-fill d-inline-flex align-items-center justify-content-center gap-2"
                    onClick={() => openControlModal(student)}
                  >
                    <FiSliders /> {t.translate("Control Wallet")}
                  </button>
                </div>
                <button
                  className="btn btn-light border d-inline-flex align-items-center justify-content-center gap-2"
                  onClick={() =>
                    Navigate(
                      `/dashboard/finance/ewallet/transaction/${student.id}`,
                    )
                  }
                >
                  <FiList /> {t.translate("View Transactions")}
                </button>
              </div>
            </div>
          </div>
        );
      }),
    [students, openChargeModal, openControlModal, Navigate],
  );

  // Payment in progress: render only the payment component, full screen.
  return (
    Pay ?? (
      <div className="ewallet row g-4">
        {/* Hero */}
        <div className="col-12">
          <div className="ewallet-hero text-white p-4 p-md-5">
            <h1 className="fw-bold mb-3 d-flex align-items-center gap-3">
              <FiCreditCard /> {t.translate("IVY E-Wallet")}
            </h1>
            <p className="mb-2 opacity-90">
              {t.translate(
                "Manage your children’s finances efficiently Cultivate smart spending habits in your child by tracking and managing their finances with a IVY E-Wallet.",
              )}
            </p>
            <p className="mb-0 opacity-90">
              {t.translate(
                "You can reserve daily meals from IVY Resturant for your children , using the IVY E-Wallet.",
              )}
            </p>
          </div>
        </div>

        {/* Students' wallets */}
        {studentCards}

        {/* Recharge options */}
        <div className="col-12">
          <h2 className="h4 fw-bold mb-3">
            {t.translate(
              "You have two Options to recharge you children E-Wallets",
            )}
          </h2>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <div className="ewallet-option d-flex align-items-center gap-3 p-3 h-100">
                <span className="ewallet-icon">
                  <FiCreditCard size={20} />
                </span>
                <span>
                  {t.translate(
                    "Online using your card by clicking on the recharge button",
                  )}
                </span>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="ewallet-option d-flex align-items-center gap-3 p-3 h-100">
                <span className="ewallet-icon">
                  <FiDollarSign size={20} />
                </span>
                <span>
                  {t.translate("Cash on the finance office in IVY Schools")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* About the E-Wallet */}
        {features.map((feature, index) => (
          <div className="col-12 col-lg-4" key={index}>
            <div className="card border-0 shadow-sm h-100 p-4">
              <span className="ewallet-icon mb-3">{feature.icon}</span>
              {feature.title && (
                <h3 className="h6 fw-bold">{t.translate(feature.title)}</h3>
              )}
              <p className="text-muted mb-0 small">
                {feature.lines.map((line) => t.translate(line)).join(" ")}
              </p>
            </div>
          </div>
        ))}

        {controlButton ? (
          /* Control modal: daily spending limit + active/suspended switch */
          <Modal
            id="ControlWalletModal"
            centered
            isOpen={isModalOpen}
            toggle={toggle}
          >
            <ModalHeader toggle={toggle}>{modalTitle}</ModalHeader>
            <ModalBody>
              <label className="form-label">
                {t.translate("Please Enter the amount.")}
              </label>
              <div className="input-group mb-4">
                <input
                  className="form-control"
                  value={Math.round(Amount) ?? 0}
                  type="number"
                  min={1}
                  onKeyDown={blockNegativeKeys}
                  onChange={onAmountChange}
                />
                <span className="input-group-text">{t.translate("LE.")}</span>
              </div>

              <div className="d-flex align-items-center justify-content-center gap-3">
                <span className={toggler ? "text-muted" : "fw-semibold"}>
                  {t.translate("Suspended")}
                </span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={toggler}
                    onChange={onToggleActive}
                  />
                  <span className="slider round"></span>
                </label>
                <span
                  className={
                    toggler ? "fw-semibold text-success" : "text-muted"
                  }
                >
                  {t.translate("Active")}
                </span>
              </div>
            </ModalBody>
            <ModalFooter className="justify-content-between">
              <button className="btn btn-light border" onClick={closeModal}>
                {t.translate("Cancel")}
              </button>
              <button
                className="btn btn-success px-4"
                disabled={!(Amount > 0)}
                onClick={onSubmitLimit}
              >
                {t.translate("Submit")}
              </button>
            </ModalFooter>
          </Modal>
        ) : (
          /* Charge modal: amount to top up, then hands off to the payment flow */
          <Modal centered isOpen={isModalOpen}>
            <ModalHeader toggle={closeModal}>{modalTitle}</ModalHeader>
            <ModalBody>
              <label className="form-label">
                {t.translate("Please Enter the amount.")}
              </label>
              <div className="input-group">
                <input
                  className="form-control"
                  value={Math.round(Amount)}
                  type="number"
                  min={1}
                  onKeyDown={blockNegativeKeys}
                  onChange={onAmountChange}
                />
                <span className="input-group-text">{t.translate("LE.")}</span>
              </div>
            </ModalBody>
            <ModalFooter className="justify-content-between">
              <button className="btn btn-light border" onClick={closeModal}>
                {t.translate("Cancel")}
              </button>
              <button
                className="btn btn-primary px-4"
                disabled={!(Amount > 0)}
                onClick={onContinuePayment}
              >
                {t.translate("Continue")}
              </button>
            </ModalFooter>
          </Modal>
        )}
      </div>
    )
  );
}
